import React, { useState } from 'react';
import { PiClock } from 'react-icons/pi';
import { useAuth } from '../../context/AuthContext';

// Shown for the whole 30-day grace window on an account that asked to close.
//
// Signing in during the window deliberately does NOT cancel the closure on its
// own — someone checking one last message shouldn't silently reverse a
// decision they meant. Nor can signing in be ignored, or there'd be no way
// back. So arriving here pauses the question and asks it, once, on every page
// until they answer.
//
// Not dismissible, unlike EmailVerifyReminder: an account that deletes itself
// on a date is not a standing nag to be waved away, and hiding it is how
// someone loses an account they meant to keep.
export default function ClosurePendingBanner({ user }) {
  const { refreshUser } = useAuth();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const closure = user?.closure;
  if (!closure?.isPending || !closure.scheduledFor) return null;

  const closeDate = new Date(closure.scheduledFor).toLocaleDateString('en-US', {
    month: 'long', day: 'numeric', year: 'numeric',
  });

  const keep = async () => {
    setError('');
    setBusy(true);
    try {
      const res = await fetch('/api/users/me/closure', { method: 'DELETE', credentials: 'include' });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.message || 'Could not cancel the closure');
      // Re-reads the user so the banner disappears and the un-paused profile
      // is reflected everywhere without a reload.
      await refreshUser?.();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex flex-wrap items-center justify-center gap-x-3 gap-y-1 border-b border-press-ink bg-[#FBF0DA] px-4 py-2.5 text-sm text-press-ink">
      <PiClock size={15} className="flex-none text-[#8A5A00]" />
      <span className="text-center">
        {error || <>Your account is set to close on <b>{closeDate}</b>. Your profile and services are hidden until then.</>}
      </span>
      <button
        type="button"
        onClick={keep}
        disabled={busy}
        className="flex-none rounded-full bg-press-ink px-3.5 py-1 text-xs font-bold text-press-paper transition hover:bg-press-ink/90 disabled:cursor-not-allowed disabled:opacity-60"
      >
        {busy ? 'Cancelling…' : 'Keep my account'}
      </button>
    </div>
  );
}
